import React, {useCallback, useEffect, useState} from 'react';

import IconButton from '@material-ui/core/IconButton';
import LinearProgress from '@material-ui/core/LinearProgress';
import {withStyles} from '@material-ui/core/styles';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import PauseRoundedIcon from '@material-ui/icons/PauseRounded';
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded';
import {Base64} from 'js-base64';

import {decryptPieceWithMetric} from '../../api/decryptUtil';
import {streamAttachment} from '../../api/zmail';

import {downloadToolbarButtonStyles, progressBarStyles} from './style';

const DownloadControlBtn = withStyles(downloadToolbarButtonStyles)(IconButton);

const DownloadProgressBar = withStyles(progressBarStyles)(LinearProgress);


/**
 * Downloader
 * @param {Object} props
 * @param {Object} props.attachment attachment object
 * @param {function} props.onCancel callback on download cancel. Ensure that
 *  this function remains consistent across renders using useCallback or etc
 * @param {function} props.onComplete callback on att download complete. Ensure
 * that this function remains consistent across renders using useCallback or etc
 * @param {function} props.onError callback on att download error. Ensure that
 *  this function remains consistent across renders using useCallback or etc
 * @param {string} searchStr url query string
 * @return {ReactElement}
 */
function AttachmentDownloader(props) {
  const {
    attachment,
    decrypter,
    onCancel,
    onComplete,
    onError,
    searchStr,
  } = props;

  const [downloadController, setDownloadController] = useState(null);

  const [isPaused, setIsPaused] = useState(false);

  const [downloadProgress, setDownloadProgress] = useState(0); // 0 - 100
  const onProgress = useCallback((bytes) => {
    // attachment size is approximately 1.75 the reported amount,
    // so estimate true byte size of attachment response body
    const multiplier = 1.75;
    const estimatedSize = attachment.size * multiplier;
    const progress =
      estimatedSize === 0 ?
                        100 :
                        Math.min((bytes * 100) / estimatedSize, 100);
    setDownloadProgress(progress);
  }, [attachment.size]);

  const onPauseToggle = (isPaused) => {
    if (isPaused) {
      setIsPaused(true);
    } else {
      setIsPaused(false);
    }
  };

  const canPauseDownload =
    downloadController &&
    downloadController.status.pausable &&
    // Pausing not supported when response ReadableStream unavailable in browser
    downloadController.status.streamSupported;

  const canCancelDownload =
    downloadController && downloadController.status.abortable;

  const handleDownloadCancel = async (e) => {
    e.stopPropagation();
    if (downloadController) {
      downloadController.abort();
      setDownloadController(null);
    } else {
      console.warn('No download controller');
    }
    onCancel();
  };

  const handleDownloadPauseToggle = async (e) => {
    e.stopPropagation();
    if (downloadController) {
      if (downloadController.status.paused) {
        downloadController.resume();
      } else {
        downloadController.pause();
      }
    } else {
      console.warn('No download controller');
    }
  };

  useEffect(() => {
    const handleDownload = async () => {
      try {
        setDownloadProgress(0);
        const qs = new URLSearchParams(searchStr);
        const getAttParams = {
          attId: attachment.id,
          tok: qs.get('tok'),
          messageId: qs.get('id'),
          dc: qs.get('dc'),
        };

        const streamController = streamAttachment(
            getAttParams,
            onProgress,
            onPauseToggle,
        );
        setDownloadController(streamController);

        const json = await streamController.read();
        const attachmentData = await decryptPieceWithMetric(
            decrypter,
            json.index,
            Base64.toUint8Array(json.data),
        );
        // ensure that download progress ends at 100%
        // in case we understimated the attachment size
        onProgress(attachment.size);
        onComplete(attachmentData);
      } catch (error) {
        console.log('error=', error);
        onError(error);
      }
    };
    handleDownload();
  }, [
    attachment.id,
    attachment.size,
    onProgress,
    searchStr,
    decrypter,
    onComplete,
    onError,
  ]);

  return (
    <>
      <DownloadProgressBar
        variant={
          downloadController?.status?.streamSupported ?
            'determinate' :
            'indeterminate'
        }
        value={downloadProgress}
      />
      <DownloadControlBtn
        size="small"
        disabled={!canPauseDownload}
        onClick={handleDownloadPauseToggle}
        aria-label={!isPaused ? 'pause' : 'resume' }
      >
        {!isPaused ? (
          <PauseRoundedIcon fontSize="inherit" />
        ) : (
          <PlayArrowRoundedIcon fontSize="inherit" />
        )}
      </DownloadControlBtn>
      <DownloadControlBtn
        size="small"
        disabled={!canCancelDownload}
        onClick={handleDownloadCancel}
        aria-label="cancel"
      >
        <CloseRoundedIcon fontSize="inherit" />
      </DownloadControlBtn>
    </>
  );
}

export default AttachmentDownloader;
